import _ from 'lodash';
import uuid4 from 'uuid/v4';

import {startSubmit, stopSubmit} from 'redux-form';
import {call, put, select, takeLatest, takeEvery} from 'redux-saga/effects';

import Raven from 'config/sentry';
import {logoutRequest} from 'ducks/auth';
import {closeAllModals} from 'ducks/modals';
import {composeActions} from 'ducks/common';
import {showToast, recordUnsuccessfulBackendCall} from 'ducks/notifications';
import {isActionConstantlyUnsuccessful} from 'ducks/notifications/utils';

import {getErrors} from 'utils/errors';
import {getWarnings} from 'utils/warnings';

export function createFetchSagaLatest(actionCreator, sdk) {
  return takeLatest(
    actionCreator.type,
    createFetchSagaWorker(actionCreator, sdk)
  );
}

export function createFetchSagaEvery(actionCreator, sdk) {
  return takeEvery(
    actionCreator.type,
    createFetchSagaWorker(actionCreator, sdk)
  );
}

function createFetchSagaWorker(actionCreator, sdk) {
  function* innerWorker(action) {
    const state = yield select();
    if (isActionConstantlyUnsuccessful(state, action)) {
      return;
    }

    // Build any extra get params like filters, sorting, pagination etc.
    let extraGetParams = {};
    if (!_.isEmpty(actionCreator.extraGetParamsSelectors)) {
      const state = yield select();
      actionCreator.extraGetParamsSelectors.forEach(extraGetParamsBuilder => {
        extraGetParams = {...extraGetParams, ...extraGetParamsBuilder(state)};
      });
    }

    const {data, errors, notFound, backendErrors} = yield request(sdk, {
      ...action.payload,
      extraGetParams
    });

    if (data) {
      yield put({
        ...actionCreator.success,
        payload: data
      });

      if (action.payload.extraActions) {
        yield* action.payload.extraActions.map(a => put(a(data)));
      }
    }

    if (errors) {
      yield put({
        ...actionCreator.failure,
        payload: {errors}
      });
    }

    if (notFound) {
      yield put({
        ...actionCreator.notFound,
        payload: {}
      });
    }

    if (backendErrors) {
      yield put(
        composeActions([
          () => actionCreator.failure,
          () => backendErrors.action,
          () => recordUnsuccessfulBackendCall({actionObject: actionCreator})
        ])
      );
    }
  }

  return innerWorker;
}

const nothing = () => ({
  data: null, // Used for 200 statuses
  errors: null, // Used for 400 statuses
  warnings: null, // Used for 200 and 400 statuses
  notFound: false, // Used for 404 statuses
  backendErrors: null // Used for 500 statuses
});

const success = data => {
  return {...nothing(), data: data || {}};
};

const failure = (errors, warnings) => {
  return {...nothing(), errors, warnings};
};

const notFound = () => {
  return {...nothing(), notFound: true};
};

const backendError = ({action}) => {
  return {...nothing(), backendErrors: {action}};
};

export function* request(sdk, sdkData) {
  try {
    const {data} = yield call(sdk, sdkData);
    return success(data);
  } catch (error) {
    if (error.response) {
      const status = error.response.status;

      if (status === 403) {
        yield put(logoutRequest());
        return nothing();
      }

      if (status === 400) {
        let errors = {};
        let warnings = {};

        if (_.get(error, 'response.data.errors')) {
          errors = getErrors(error.response.data);
        }

        if (_.get(error, 'response.data.warnings')) {
          warnings = {
            ...getWarnings(error.response.data.warnings),
            _meta: error.response.data.warnings
          };
        }

        return failure(errors, warnings);
      }

      if (status === 404) {
        yield put(closeAllModals());
        return notFound();
      }

      if (status === 500) {
        // Send sentry error
        Raven.captureException(error, {
          extra: {
            sdk,
            sdkData,
            error
          }
        });

        // Log the error in the local console
        console.error(error);

        const toastObject = {
          uuid: uuid4(),
          title: 'Error 500',
          text:
            "Something went wrong. This error is unexpected and we're working on fixing it!",
          color: 'red'
        };

        // Record the failed backend call so we can prevent the user from sending that call multiple times.
        return backendError({
          action: showToast(toastObject)
        });
      }

      throw error;
    }

    throw error;
  }
}

export const createPostSagaLatest = (actionCreator, sdk) => {
  return takeLatest(
    actionCreator.type,
    createPostSagaWorker(actionCreator, sdk)
  );
};

export const createPostSagaEvery = (actionCreator, sdk) => {
  return takeEvery(
    actionCreator.type,
    createPostSagaWorker(actionCreator, sdk)
  );
};

// ActionCreator should carry the form name with it (if any)
const createPostSagaWorker = (actionCreator, sdk) => {
  function* innerWorker(action) {
    const state = yield select();

    if (isActionConstantlyUnsuccessful(state, action)) {
      return;
    }

    if (actionCreator.formName) {
      yield put(startSubmit(actionCreator.formName));
    }

    const {data, errors, warnings, notFound, backendErrors} = yield request(
      sdk,
      action.payload
    );

    if (notFound) {
      if (actionCreator.formName) {
        yield put(stopSubmit(actionCreator.formName));
      }

      throw new Error(
        `Tried to perform ${
          action.type
        } but the API returned 404: Page not found.`
      );
    }

    if (data) {
      if (actionCreator.formName) {
        yield put(stopSubmit(actionCreator.formName));
      }

      yield put({
        ...actionCreator.success,
        payload: data
      });

      if (action.payload.extraActions) {
        yield* action.payload.extraActions.map(a => put(a(data)));
      }

      if (action.payload.successExtraActions) {
        yield* action.payload.successExtraActions.map(a => put(a(data)));
      }
    }

    if (errors || warnings) {
      if (actionCreator.formName) {
        yield put(stopSubmit(actionCreator.formName, errors));
      }

      yield put({
        ...actionCreator.failure,
        payload: {errors, warnings}
      });

      if (action.payload.failureExtraActions) {
        yield* action.payload.failureExtraActions.map(a =>
          put(a({errors, warnings}))
        );
      }
    }

    if (backendErrors) {
      if (actionCreator.formName) {
        yield put(stopSubmit(actionCreator.formName, {}));
      }

      yield put(
        composeActions([
          () => actionCreator.failure,
          () => backendErrors.action,
          () => recordUnsuccessfulBackendCall({actionObject: actionCreator})
        ])
      );
    }
  }

  return innerWorker;
};
